/**
 * fromtFrostVaultIntegration — high-level helpers around the Monero
 * FROST WASM bridge for Vultisig vault bake-in.
 *
 * Mirrors the layout of `orchardFrostVaultIntegration` so future
 * features that need to work for both shielded chains can share the
 * same patterns. The public surface here is intentionally narrow:
 *
 *   - `generateFromtBundlesForFreshVault({ devices, minSigners, ... })`
 *     drives an in-tab multi-party FROST Ed25519 DKG and returns one
 *     `MoneroKeyShareBundle` per device.
 *
 *   - `storeFromtBundle / loadFromtBundle / hasFromtBundle` persist a
 *     single device's bundle in `localStorage` keyed by the vault UID,
 *     same convention as the Orchard store.
 *
 *   - `parseMoneroBundleSummary` exposes view-key / birthday / network
 *     for UI summaries without touching the WASM scanner.
 *
 * The DKG runner emulates the relay loop locally: every session
 * exposes `takeMsg` (drains its outbox) and `feed` (delivers an
 * inbound message). `msgReceiver(msg, idx)` tells us which party a
 * message is destined for — index `0` returns the recipient (or first
 * for broadcasts) and incrementing the index walks the recipient list
 * for broadcast packets. We loop until every session reports complete
 * or progress stalls.
 */

import {
	initFromtFrostWasm,
	encodeFromtPartiesData,
	networkByte,
	parseKeyShareBundleHeader,
	type FromtNetwork,
	type FromtPartyEntry,
} from './fromtFrostBridge';
import {
	runInitiatorMoneroDkgCeremony,
	runJoinerMoneroDkgCeremony,
	type MoneroDkgCeremonyResult,
	type MoneroDkgCeremonyCallbacks,
	type MoneroDkgSessionParams,
} from '../../../../services/moneroDkgCeremony';
import type { CosignTransport } from '../../../../services/cosignTransports';

/** Legacy localStorage prefix — only used to sweep stale entries from
 *  browsers upgraded across the May 2026 privacy fix. Bundles are now
 *  in-memory only (see `_fromtBundles` below); the user persists them
 *  exclusively by downloading a `.wult` file. */
const LEGACY_STORAGE_PREFIX = 'wb32:fromt-frost-bundle:';
const LEGACY_KEEP_AS_IS_PREFIX = 'wb32:fromt-frost-keep-as-is:';
const DEFAULT_NETWORK: FromtNetwork = 'mainnet';
const MAX_DKG_DRIVE_LOOPS = 256;

/** In-memory store for the active session only. Cleared on tab close
 *  — the `.wult` file is the canonical persistence layer. The earlier
 *  build pushed every bundle into `localStorage` keyed by vault UID,
 *  which produced a privacy leak: plain `.vult` files re-loaded later
 *  appeared to "already have Monero" because the cache from a
 *  previous DKG session was still on disk. */
const _fromtBundles = new Map<string, MoneroKeyShareBundle>();
/** Session-scoped "user declined to set up Monero FROST for this
 *  vault" flag. Not persisted — declining is no longer sticky across
 *  reloads. */
const _fromtKeepAsIs = new Set<string>();

/**
 * Sweep legacy localStorage entries left over from before the May
 * 2026 privacy fix. Wallet key material must never live in
 * localStorage. Tries to run only once per tab (cheap), but the
 * actual sweep is always safe to repeat. `__resetForTests` clears
 * the guard so test suites can exercise the sweep deterministically.
 */
let _legacySweepDone = false;
export function __resetFromtSweepGuardForTests(): void {
	_legacySweepDone = false;
}
export function sweepLegacyFromtLocalStorage(): void {
	if (_legacySweepDone) return;
	_legacySweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		const doomed: string[] = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (!key) continue;
			if (key.startsWith(LEGACY_STORAGE_PREFIX)
				|| key.startsWith(LEGACY_KEEP_AS_IS_PREFIX)) {
				doomed.push(key);
			}
		}
		for (const key of doomed) {
			try { localStorage.removeItem(key); } catch { /* */ }
		}
		if (doomed.length > 0) {
			console.info('[fromt-frost] swept %d legacy localStorage entries',
				doomed.length);
		}
	} catch (err) {
		console.warn('[fromt-frost] legacy localStorage sweep failed:',
			(err as Error).message);
	}
}

/**
 * One device's bake-in result. `bundleBytes` is the binary blob
 * produced by the WASM (same format `frosty-lib::KeyShareBundle`
 * serialises). `partyName` matches the `localPartyId` of the SDK
 * vault on this device, so the share file written for that device
 * carries the matching Monero share. `frostId` is the FROST
 * identifier (1-indexed, monotonic) — useful when threshold-signing
 * needs to know which Lagrange coefficient applies.
 */
export interface MoneroKeyShareBundle {
	partyName: string;
	frostId: number;
	bundleBytes: Uint8Array;
	network: FromtNetwork;
	birthday: bigint;
	bakedAtCreation: true;
	ceremonyVersion: number;
}

export interface MoneroBundleSummary {
	network: FromtNetwork;
	birthday: bigint;
	viewKey: Uint8Array;
	bundleBytesLength: number;
}

const CURRENT_FROMT_BUNDLE_VERSION = 1;

function networkNameFromByte(byte: number): FromtNetwork {
	switch (byte) {
		case 0x12: return 'mainnet';
		case 0x35: return 'testnet';
		case 0x18: return 'stagenet';
		default: return 'mainnet';
	}
}

/**
 * Drive a complete in-tab FROST Ed25519 DKG across N sessions.
 *
 * Returns one `MoneroKeyShareBundle` per device, in `partyNames`
 * order. The caller is expected to pass `participantPartyIds` aligned
 * with the SDK vault's `localPartyId` list so the i-th SDK vault gets
 * the i-th Monero bundle.
 *
 * @param params.devices - total number of vault devices (>=2)
 * @param params.minSigners - threshold (T-of-N) for spending
 * @param params.network - 'mainnet' | 'testnet' | 'stagenet'
 * @param params.birthday - block height at vault creation; used as
 *   scan start later
 * @param params.participantPartyIds - optional party-name list. If
 *   omitted we synthesise stable names (`share-1`...`share-N`).
 * @param params.signal - optional AbortSignal to cancel mid-DKG.
 */
export async function generateFromtBundlesForFreshVault({
	devices,
	minSigners,
	network = DEFAULT_NETWORK,
	birthday = 0n,
	participantPartyIds,
	signal,
}: {
	devices: number;
	minSigners: number;
	network?: FromtNetwork;
	birthday?: bigint | number;
	participantPartyIds?: string[];
	signal?: AbortSignal;
}): Promise<MoneroKeyShareBundle[]> {
	if (devices < 2) {
		throw new Error('Monero FROST DKG needs at least 2 devices');
	}
	const t = Math.max(2, Math.min(minSigners, devices));
	const birthdayBig = typeof birthday === 'bigint'
		? birthday
		: BigInt(Math.max(0, Math.floor(birthday)));

	const partyNames = (participantPartyIds && participantPartyIds.length === devices)
		? [...participantPartyIds]
		: Array.from({ length: devices }, (_, i) => `share-${i + 1}`);
	const parties: FromtPartyEntry[] = partyNames.map((name, i) => ({
		frostId: i + 1,
		name,
	}));

	const wasm = await initFromtFrostWasm();
	const partiesData = encodeFromtPartiesData(parties);
	const networkByteVal = networkByte(network);

	const setupBytes = wasm.fromtDkgSetupMsgNew(
		devices,
		t,
		partiesData,
		networkByteVal,
		birthdayBig,
	);

	const sessions = partyNames.map(name =>
		wasm.FromtDkgSession.fromSetup(setupBytes, name, networkByteVal, birthdayBig),
	);
	const completed = new Set<number>();
	const indexByName = new Map(partyNames.map((n, i) => [n, i]));

	try {
		for (let loop = 0; loop < MAX_DKG_DRIVE_LOOPS; loop++) {
			if (signal?.aborted) {
				const err = new Error('Monero FROST DKG aborted');
				err.name = 'AbortError';
				throw err;
			}
			let progress = false;
			for (let i = 0; i < devices; i++) {
				let msg: Uint8Array | undefined;
				while ((msg = sessions[i].takeMsg())) {
					progress = true;
					// `takeMsg()` returns `[recipient_u16_le][payload...]`
					// (the WASM relay's outbox frame) but `feed()` needs
					// `[sender_u16_le][payload...]` to drive the
					// recipient's protocol. Rewrap before delivery.
					const payload = msg.subarray(2);
					const senderFrostId = i + 1;
					const wrapped = new Uint8Array(2 + payload.length);
					wrapped[0] = senderFrostId & 0xff;
					wrapped[1] = (senderFrostId >> 8) & 0xff;
					wrapped.set(payload, 2);
					// Walk the recipient list. For unicast, only index 0
					// returns a name. For broadcast, msgReceiver advances
					// over every other party.
					for (let idx = 0; idx < devices; idx++) {
						const recipient = sessions[i].msgReceiver(msg, idx);
						if (!recipient) break;
						const recipientIdx = indexByName.get(recipient);
						if (recipientIdx === undefined) {
							throw new Error(
								`Monero FROST DKG produced message addressed to unknown party "${recipient}"`,
							);
						}
						if (recipientIdx === i) continue;
						const finished = sessions[recipientIdx].feed(wrapped);
						if (finished) completed.add(recipientIdx);
					}
				}
			}
			if (completed.size === devices) break;
			if (!progress) {
				throw new Error(
					`Monero FROST DKG stalled after loop ${loop}: ${completed.size}/${devices} complete`,
				);
			}
		}

		if (completed.size !== devices) {
			throw new Error(
				`Monero FROST DKG did not finish in ${MAX_DKG_DRIVE_LOOPS} loops (${completed.size}/${devices})`,
			);
		}

		const bundles = sessions.map((s, i): MoneroKeyShareBundle => {
			const bytes = s.result();
			// Sanity: the WASM bundle's network/birthday must round-trip.
			const header = parseKeyShareBundleHeader(bytes);
			return {
				partyName: partyNames[i],
				frostId: i + 1,
				bundleBytes: bytes,
				network: networkNameFromByte(header.networkByte),
				birthday: header.birthday,
				bakedAtCreation: true,
				ceremonyVersion: CURRENT_FROMT_BUNDLE_VERSION,
			};
		});
		return bundles;
	} finally {
		for (const s of sessions) {
			try { s.free(); } catch { /* WASM already disposed — fine */ }
		}
	}
}

// ── Storage (in-memory only — see `_fromtBundles` above) ─────────────

export function storeFromtBundle(vaultUid: string, bundle: MoneroKeyShareBundle): void {
	if (!vaultUid || vaultUid.length < 8) {
		throw new Error('storeFromtBundle: vault UID must be >= 8 chars');
	}
	// Make sure no legacy localStorage entry is hanging around for
	// this UID before we add the new in-memory entry.
	sweepLegacyFromtLocalStorage();
	// Defensive copy of the bytes so a caller mutating the original
	// `Uint8Array` later doesn't silently corrupt our cached share.
	_fromtBundles.set(vaultUid, {
		partyName: bundle.partyName,
		frostId: bundle.frostId,
		network: bundle.network,
		birthday: bundle.birthday,
		bundleBytes: new Uint8Array(bundle.bundleBytes),
		bakedAtCreation: bundle.bakedAtCreation,
		ceremonyVersion: bundle.ceremonyVersion,
	});
}

export function loadFromtBundle(vaultUid: string): MoneroKeyShareBundle | null {
	if (!vaultUid) return null;
	const cached = _fromtBundles.get(vaultUid);
	if (!cached) return null;
	// Return a defensive copy so the caller can't mutate the cached
	// entry through the returned reference.
	return {
		partyName: cached.partyName,
		frostId: cached.frostId,
		network: cached.network,
		birthday: cached.birthday,
		bundleBytes: new Uint8Array(cached.bundleBytes),
		bakedAtCreation: cached.bakedAtCreation,
		ceremonyVersion: cached.ceremonyVersion ?? CURRENT_FROMT_BUNDLE_VERSION,
	};
}

export function hasFromtBundle(vaultUid: string): boolean {
	if (!vaultUid) return false;
	return _fromtBundles.has(vaultUid);
}

export function removeFromtBundle(vaultUid: string): void {
	if (!vaultUid) return;
	_fromtBundles.delete(vaultUid);
}

/**
 * Drop every in-memory Monero bundle and decline flag. Intended for
 * "logout" / "switch vault" UI affordances and for test cleanup. The
 * legacy localStorage sweep also runs so any stale data left behind
 * by older builds is purged at the same time.
 */
export function clearAllFromtBundles(): void {
	_fromtBundles.clear();
	_fromtKeepAsIs.clear();
	sweepLegacyFromtLocalStorage();
}

/**
 * Lightweight summary for UI without instantiating the WASM scanner.
 * Just decodes the binary header.
 */
export function parseMoneroBundleSummary(bundle: MoneroKeyShareBundle): MoneroBundleSummary {
	const header = parseKeyShareBundleHeader(bundle.bundleBytes);
	return {
		network: networkNameFromByte(header.networkByte),
		birthday: header.birthday,
		viewKey: header.viewKey,
		bundleBytesLength: bundle.bundleBytes.length,
	};
}

// ── Relay-based DKG vault helpers ────────────────────────────────────
//
// Mirrors the Orchard `runProperOrchardDkgInitiator` / `…Joiner`
// helpers: thin wrappers around the relay ceremony in
// `moneroDkgCeremony.ts` that convert the ceremony result into a
// `MoneroKeyShareBundle`, persist it for the active device, and return
// the bundle so the caller can attach it to peer vault objects when
// producing fresh `.wult` exports.

export interface RunMoneroDkgVaultOptions {
	vaultUid: string;
	session: MoneroDkgSessionParams;
	myPartyId: string;
	otherPartyIds: string[];
	minSigners: number;
	network?: FromtNetwork;
	birthday?: bigint | number;
	callbacks?: MoneroDkgCeremonyCallbacks;
	transport?: CosignTransport;
}

export interface RunMoneroDkgJoinerVaultOptions {
	vaultUid: string;
	session: MoneroDkgSessionParams;
	myPartyId: string;
	initiatorPartyId: string;
	callbacks?: MoneroDkgCeremonyCallbacks;
	transport?: CosignTransport;
}

function ceremonyResultToBundle(
	res: MoneroDkgCeremonyResult,
): MoneroKeyShareBundle {
	return {
		partyName: res.myPartyId,
		frostId: res.frostId,
		bundleBytes: res.bundleBytes,
		network: res.network,
		birthday: res.birthday,
		bakedAtCreation: true,
		ceremonyVersion: CURRENT_FROMT_BUNDLE_VERSION,
	};
}

/**
 * Initiator entry point for the relay-mediated Monero DKG ceremony.
 * The caller is responsible for rendering the `WB32MDKG:` invitation
 * QR (via `generateMoneroDkgQR`) so joiners can connect via the
 * Cosigner. On success the bundle is persisted locally and returned.
 */
export async function runMoneroDkgInitiator(
	options: RunMoneroDkgVaultOptions,
): Promise<MoneroKeyShareBundle> {
	const result = await runInitiatorMoneroDkgCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		otherPartyIds: options.otherPartyIds,
		minSigners: options.minSigners,
		vaultUid: options.vaultUid,
		network: options.network,
		birthday: options.birthday,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	const bundle = ceremonyResultToBundle(result);
	storeFromtBundle(options.vaultUid, bundle);
	return bundle;
}

/**
 * Joiner entry point invoked from the Cosigner once a `WB32MDKG:` QR
 * has been scanned/loaded. Same persistence behaviour as the
 * initiator wrapper.
 */
export async function runMoneroDkgJoiner(
	options: RunMoneroDkgJoinerVaultOptions,
): Promise<MoneroKeyShareBundle> {
	const result = await runJoinerMoneroDkgCeremony({
		session: options.session,
		myPartyId: options.myPartyId,
		initiatorPartyId: options.initiatorPartyId,
		vaultUid: options.vaultUid,
		callbacks: options.callbacks,
		transport: options.transport,
	});
	const bundle = ceremonyResultToBundle(result);
	storeFromtBundle(options.vaultUid, bundle);
	return bundle;
}

/**
 * Record that the user explicitly declined to set up Monero FROST for
 * this vault. The flag is advisory — every UI surface that nags about
 * a missing bundle should check this first and fall silent. The flag
 * is session-scoped (in-memory): a fresh browser load starts with no
 * decline so the user can change their mind without their previous
 * decline silently carrying over.
 */
export function markFromtFrostKeepAsIs(vaultUid: string): void {
	if (!vaultUid || vaultUid.length < 8) return;
	_fromtKeepAsIs.add(vaultUid);
}

export function isFromtFrostKeepAsIs(vaultUid: string): boolean {
	if (!vaultUid) return false;
	return _fromtKeepAsIs.has(vaultUid);
}

/**
 * Derive a 1-of-1 Monero `MoneroKeyShareBundle` from a 32-byte seed.
 *
 * Phrase-connected wallets (no `.vult` / `.wult` loaded) feed the
 * BIP-39 seed (typically the first 32 bytes of the BIP-39 seed, or a
 * SLIP-21 derived 32-byte node) through the WASM helper. The resulting
 * bundle has the same binary layout as a multi-party FROST share, so
 * every downstream consumer (`MoneroAccount`, the spend pipeline,
 * receive QR, balance hook) works identically to vault mode — phrase
 * users just happen to be the only signer.
 *
 * @param seed 32-byte seed (will be passed straight to the WASM)
 * @param partyName party label persisted in the bundle (defaults to
 *   `phrase`)
 * @param network mainnet | testnet | stagenet (defaults to mainnet)
 * @param birthday block height to scan from (defaults to 0 = genesis)
 */
export async function deriveMoneroBundleFromSeed({
	seed,
	partyName = 'phrase',
	network = DEFAULT_NETWORK,
	birthday = 0n,
}: {
	seed: Uint8Array;
	partyName?: string;
	network?: FromtNetwork;
	birthday?: bigint | number;
}): Promise<MoneroKeyShareBundle> {
	if (!seed || seed.length !== 32) {
		throw new Error('deriveMoneroBundleFromSeed: seed must be exactly 32 bytes');
	}
	const wasm = await initFromtFrostWasm();
	if (typeof wasm.fromt_singleparty_bundle_from_seed !== 'function') {
		throw new Error(
			'Monero FROST WASM does not expose '
			+ '`fromt_singleparty_bundle_from_seed` — rebuild '
			+ 'frosty-lib (ci/build-frost-wasm.sh --force-build).',
		);
	}
	const birthdayBig = typeof birthday === 'bigint'
		? birthday
		: BigInt(Math.max(0, Math.floor(birthday)));
	const networkByteVal = networkByte(network);
	const bundleBytes = wasm.fromt_singleparty_bundle_from_seed(
		seed,
		networkByteVal,
		birthdayBig,
	);
	if (!bundleBytes || bundleBytes.length === 0) {
		throw new Error('Monero single-party bundle derivation produced no bytes');
	}
	const header = parseKeyShareBundleHeader(bundleBytes);
	return {
		partyName,
		frostId: 1,
		bundleBytes,
		network: networkNameFromByte(header.networkByte),
		birthday: header.birthday,
		bakedAtCreation: true,
		ceremonyVersion: CURRENT_FROMT_BUNDLE_VERSION,
	};
}
